import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { useLang, pick } from "../../lib/i18n";

export default function Gallery({ gallery }) {
  const { t, lang } = useLang();
  const [selected, setSelected] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-24 sm:py-32 bg-[#FBF9F5]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.3em] font-sans font-semibold text-[#9A7B66] mb-5"
        >
          {t("gallery.eyebrow")}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl mb-14"
        >
          {t("gallery.title")}
        </motion.h2>

        <div className="columns-2 md:columns-3 gap-4 [column-fill:balance]">
          {(gallery || []).map((item, i) => (
            <motion.button
              key={item.id || i}
              data-testid={`gallery-item-${i}`}
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.7, delay: (i % 3) * 0.1 }}
              onClick={() => setSelected(i)}
              className="relative block w-full mb-4 break-inside-avoid rounded-xl overflow-hidden group"
            >
              <img
                src={mediaUrl(item.image_url)}
                alt={item.image_alt || pick(item, "caption", lang) || "Nusadira gallery"}
                title={item.image_title || pick(item, "caption", lang) || undefined}
                aria-description={item.image_description || undefined}
                loading="lazy"
                style={{ objectPosition: item.image_focal_point || "50% 0%" }}
                className="w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              />
              {item.image_description && <span className="sr-only">{item.image_description}</span>}
              <div className="absolute inset-0 bg-[#1E1A17]/0 group-hover:bg-[#1E1A17]/35 transition-colors duration-500" />
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selected !== null && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#1E1A17]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
            onClick={() => setSelected(null)}
          >
            <button
              data-testid="lightbox-close"
              className="absolute top-5 right-5 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              onClick={() => setSelected(null)}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              data-testid="lightbox-prev"
              className="absolute left-3 sm:left-8 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelected((selected - 1 + gallery.length) % gallery.length);
              }}
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <motion.img
              key={selected}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              src={mediaUrl(gallery[selected]?.image_url)}
              alt={gallery[selected]?.image_alt || pick(gallery[selected], "caption", lang) || "Gallery image"}
              title={gallery[selected]?.image_title || pick(gallery[selected], "caption", lang) || undefined}
              aria-description={gallery[selected]?.image_description || undefined}
              className="max-h-[82vh] max-w-full rounded-xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            {(gallery[selected]?.image_caption || pick(gallery[selected], "caption", lang)) && (
              <p data-testid="gallery-lightbox-caption" className="absolute bottom-5 right-5 max-w-[75%] rounded-sm bg-[#1E1A17]/75 backdrop-blur-sm px-4 py-2 text-xs sm:text-sm text-[#F4EFEA] font-sans text-right">
                {gallery[selected]?.image_caption || pick(gallery[selected], "caption", lang)}
              </p>
            )}
            <button
              data-testid="lightbox-next"
              className="absolute right-3 sm:right-8 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                setSelected((selected + 1) % gallery.length);
              }}
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
